import { HttpClient } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'

import { Observable, map } from 'rxjs'

import {
  AgentWorkflowRequest,
  BusinessAgentsRequest,
  CreatePlanRequest,
  InitialMessageRequest,
  IntentRequest,
  MapDestinationRequest,
  Message,
  PlanRequest,
  PromptForAgentRequest,
  PromptForStepRequest,
} from '@flowx/designer/modules/new-ai/common/ai.dictionary'
import { ApiHttpService } from '@flowx/designer/shared/api/api-http.service'

/**
 * Feature toggle: switch between v1 (graph-based) and v2 (deep agent) coordinator.
 *
 * When `true`, getSSEIntent and getSSEPlan route through the consolidated
 * v2 /chat endpoint. All other methods that aren't yet consolidated
 * (actions, map_destination, next_steps, automate, prompt, agent) stay on v1.
 *
 * Set to `true` to test the deep agent coordinator.
 */
const USE_DEEP_AGENT_COORDINATOR = false

@Injectable({ providedIn: 'root' })
export class AiCoordinatorApi extends ApiHttpService {
  #coordinatorV1 = `ai-engine/agents/coordinator`
  #coordinatorV2 = `ai-engine/agents/coordinator/v2`

  /** Active base path — switches between v1 and v2 for eligible endpoints. */
  get #coordinator(): string {
    return USE_DEEP_AGENT_COORDINATOR ? this.#coordinatorV2 : this.#coordinatorV1
  }

  constructor() {
    super(inject('BASE_URL' as any), inject(HttpClient))
  }

  // ---------------------------------------------------------------------------
  // Intent — v1: /intent | v2: /chat
  // ---------------------------------------------------------------------------

  getSSEIntent(payload: IntentRequest): Observable<Omit<Message, 'author' | 'feedback'>> {
    if (USE_DEEP_AGENT_COORDINATOR) {
      // v2 consolidates intent + plan into a single /chat endpoint
      const v2Payload = {
        user_message: payload.user_message,
        app_location: payload.app_location,
        screen_context: payload.screen_context,
        session_id: payload.session_id,
        autonomy_level: payload.autonomy_level || 'low',
      }
      return this.streamArray(`${this.#coordinatorV2}/chat`, v2Payload).pipe(this.#mapSSEResponse())
    }
    return this.streamArray(`${this.#coordinatorV1}/intent`, payload).pipe(this.#mapSSEResponse())
  }

  // ---------------------------------------------------------------------------
  // Plan — v1: /plan | v2: /chat
  // ---------------------------------------------------------------------------

  getSSEPlan(payload: PlanRequest): Observable<Omit<Message, 'author' | 'feedback'>> {
    if (USE_DEEP_AGENT_COORDINATOR) {
      // v2 uses /chat for plan generation; /resume for interrupt follow-ups
      if (payload.human_feedback) {
        const resumePayload = {
          session_id: payload.session_id,
          decisions: payload.human_feedback,
        }
        return this.streamArray(`${this.#coordinatorV2}/resume`, resumePayload).pipe(
          this.#mapSSEResponse()
        )
      }
      const v2Payload: Record<string, any> = {
        user_message: payload.user_message,
        session_id: payload.session_id,
        context: payload.context,
        current_plan: payload.current_plan,
        plan_type: payload.plan_type || 'application',
        autonomy_level: 'low',
      }
      if (payload.document_id) {
        v2Payload['document_id'] = payload.document_id
      }
      return this.streamArray(`${this.#coordinatorV2}/chat`, v2Payload).pipe(this.#mapSSEResponse())
    }
    return this.streamArray(`${this.#coordinatorV1}/plan`, payload).pipe(this.#mapSSEResponse())
  }

  // ---------------------------------------------------------------------------
  // Actions — always v1 (not yet consolidated into deep agent)
  // ---------------------------------------------------------------------------

  getSSEActions(payload: InitialMessageRequest): Observable<Omit<Message, 'author' | 'feedback'>> {
    return this.streamArray(`${this.#coordinatorV1}/actions`, payload).pipe(this.#mapSSEResponse())
  }

  // ---------------------------------------------------------------------------
  // Map destination — always v1
  // ---------------------------------------------------------------------------

  getSSEMapDestination(
    payload: MapDestinationRequest
  ): Observable<Omit<Message, 'author' | 'feedback'>> {
    return this.streamArray(`${this.#coordinatorV1}/map_destination`, payload).pipe(
      this.#mapSSEResponse()
    )
  }

  // ---------------------------------------------------------------------------
  // Next steps — always v1
  // ---------------------------------------------------------------------------

  getSSENextStepSelectedPlan(
    payload: MapDestinationRequest
  ): Observable<Omit<Message, 'author' | 'feedback'>> {
    return this.streamArray(`${this.#coordinatorV1}/next_steps`, payload).pipe(
      this.#mapSSEResponse()
    )
  }

  // ---------------------------------------------------------------------------
  // Business agents / automate — always v1
  // ---------------------------------------------------------------------------

  getSSEBusinessAgents(
    payload: BusinessAgentsRequest
  ): Observable<Omit<Message, 'author' | 'feedback'>> {
    return this.streamArray(`${this.#coordinatorV1}/automate`, payload).pipe(this.#mapSSEResponse())
  }

  // ---------------------------------------------------------------------------
  // Prompt for agent — always v1
  // ---------------------------------------------------------------------------

  getSSEPromptForAgent(
    payload: PromptForAgentRequest
  ): Observable<Omit<Message, 'author' | 'feedback'>> {
    return this.streamArray(`${this.#coordinatorV1}/prompt`, payload).pipe(this.#mapSSEResponse())
  }

  getSSEPromptForStep(
    payload: PromptForStepRequest
  ): Observable<Omit<Message, 'author' | 'feedback'>> {
    return this.streamArray(`${this.#coordinatorV1}/prompt`, payload).pipe(this.#mapSSEResponse())
  }

  // ---------------------------------------------------------------------------
  // Agent workflow — always v1
  // ---------------------------------------------------------------------------

  getSSEAgentWorkflow(
    payload: AgentWorkflowRequest
  ): Observable<Omit<Message, 'author' | 'feedback'>> {
    return this.streamArray(`${this.#coordinatorV1}/agent`, payload).pipe(this.#mapSSEResponse())
  }

  // ---------------------------------------------------------------------------
  // Plan CRUD — uses v2 prefix when toggle is on (same REST interface)
  // ---------------------------------------------------------------------------

  createPlan(payload: CreatePlanRequest): Observable<any> {
    return this.post(`${this.#coordinator}/plan/save`, payload).pipe(
      map((response: any) => response)
    )
  }

  getAllPlans(payload: {
    application_id: string
    application_version_id: string
    process_id: string
  }): Observable<any> {
    return this.post(`${this.#coordinator}/plan/get_all`, payload).pipe(
      map((response: any) => response)
    )
  }

  getPlan(payload: { plan_id: string }): Observable<any> {
    return this.post(`${this.#coordinator}/plan/get`, payload).pipe(
      map((response: any) => response)
    )
  }

  updatePlan(payload: {
    plan_id: string
    plan: any
    status: string
    author: string
  }): Observable<any> {
    return this.put(`${this.#coordinator}/plan/update`, payload).pipe(
      map((response: any) => response)
    )
  }

  deletePlan(payload: { plan_id: string }): Observable<any> {
    return this.post(`${this.#coordinatorV1}/plan/delete`, payload).pipe(
      map((response: any) => response)
    )
  }

  // ---------------------------------------------------------------------------
  // SSE streaming helpers
  // ---------------------------------------------------------------------------

  #mapSSEResponse() {
    return (source: Observable<any>): Observable<Omit<Message, 'author' | 'feedback'>> =>
      source.pipe(
        map((response: any) => {
          if (response) {
            return response
          }
          try {
            const responseObj = JSON.parse(response)
            if (responseObj?.detail?.error_message) {
              throw new Error(responseObj.detail.error_message)
            }
          } catch (e) {
            return e ?? response
          }
        }),
        map((answerStr) => ({ message: answerStr }))
      )
  }
}
